import * as R from 'ramda';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../components/Hooks/useWidth';
import storage from '../../../hooks/useStorage/useStorage';
import PATHS from '../../../routes/routes.const';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { OrderBookOrder, OrderBookStorage } from '../../../stores/OrderBook/orderBook.types';
import { orderBookPersistStorage } from '../../../stores/OrderBook/utils/orderBookPersistStorage';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { StorageEnum } from '../../context/storage';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import { OrderView } from '../../orders/ordersView.types';

interface OrderBookResult {
  openOrderBook: () => void;
  closeOrderBook: () => void;
  onCatalogs: boolean;
  onEdit: boolean;
  selectedOrderIsSuggested: boolean;
  initializeOrderBookStore: (offline?: UsedInOfflineMode) => void;
  allOrders: OrderBookOrder[];
  customerIds: string[];
}

interface UseOrderBookParams extends UsedInOfflineMode {}

export default function useOrderBook({
  isOfflineMode = false,
}: UseOrderBookParams = {}): OrderBookResult {
  const { mobileWidth } = useWidth();
  const { team, brandId } = useApplicationContext();
  const history = useHistory();
  const teamId = team?.id || 'anonymous';

  const [
    handleOpenOrderBook,
    handleOpenOrderBookModal,
    handleCloseOrderBook,
    selectedOrder,
    orders,
    setOrderBookOfflineMode,
  ] = useOrderBookStore(
    useShallow((state) => [
      state.handleOpenOrderBook,
      state.handleOpenOrderBookModal,
      state.handleCloseOrderBook,
      state.selectedOrder,
      state.orders,
      state.setOrderBookOfflineMode,
    ]),
  );
  const selectedOrderIsSuggested = selectedOrder?.status === 'SUGGESTED';

  const currentRoute: string = history.location.pathname;
  const onCatalogs: boolean = currentRoute.includes(PATHS.main.catalogs.path);
  const onOrders: boolean = currentRoute.includes(PATHS.main.orders.path);
  const onEdit: boolean = currentRoute.includes(OrderView.EDIT) && onOrders;
  const allOrders: OrderBookOrder[] = useMemo(
    () => (selectedOrder ? [selectedOrder, ...orders] : []),
    [selectedOrder, orders],
  );
  const customerIds: string[] = useMemo(
    () =>
      Object.values(
        R.pipe(R.map(R.pathOr(undefined, ['customer', 'id'])), R.reject(R.isNil))(allOrders),
      ) as string[],
    [allOrders],
  );

  const openOrderBook = useMemo(
    () => (mobileWidth ? handleOpenOrderBookModal : handleOpenOrderBook),
    [handleOpenOrderBook, handleOpenOrderBookModal, mobileWidth],
  );

  const closeOrderBook = () => {
    handleCloseOrderBook();
  };

  const initializeOrderBookStore = () => {
    const { persist, getState } = useOrderBookStore;

    persist.setOptions({
      storage: createJSONStorage<OrderBookStorage>(() =>
        orderBookPersistStorage(teamId, isOfflineMode),
      ),
      onRehydrateStorage: () => () => {
        if (isOfflineMode) {
          setOrderBookOfflineMode(true);
          return;
        }

        if (brandId) {
          getState().validateRehydratedOrders();
        }
      },
    });
    persist.rehydrate();
  };

  useEffect(() => {
    const onStorageOrderBook = () => {
      const { hasItemBy, handleNetworkMode } = storage;

      const hasOrderBookStorage = hasItemBy(
        handleNetworkMode(StorageEnum.ORDER_BOOK, isOfflineMode),
        teamId,
      );

      if (hasOrderBookStorage) {
        useOrderBookStore.persist.rehydrate();
      }
    };

    window.addEventListener('storage', onStorageOrderBook);
    return () => window.removeEventListener('storage', onStorageOrderBook);
  }, [teamId, isOfflineMode]);

  return {
    openOrderBook,
    closeOrderBook,
    onCatalogs,
    onEdit,
    allOrders,
    customerIds,
    initializeOrderBookStore,
    selectedOrderIsSuggested,
  };
}
